import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  earningsCalendarApi,
  earningsHistoryApi,
  earningsSummaryApi,
  earningsRefreshApi,
} from '../api/earnings'

// Query keys
const EARNINGS_CALENDAR_KEY = ['earnings', 'calendar']
const EARNINGS_HISTORY_KEY = ['earnings', 'history']
const EARNINGS_SUMMARY_KEY = ['earnings', 'summary']

// ===== CALENDAR HOOKS =====

export function useEarningsCalendar(daysAhead: number = 90) {
  return useQuery({
    queryKey: [...EARNINGS_CALENDAR_KEY, daysAhead],
    queryFn: () => earningsCalendarApi.get(daysAhead),
    staleTime: 1000 * 60 * 60, // 1 hour
  })
}

// ===== HISTORY HOOKS =====

export function useEarningsHistory(symbol: string, limit: number = 20) {
  return useQuery({
    queryKey: [...EARNINGS_HISTORY_KEY, symbol, limit],
    queryFn: () => earningsHistoryApi.get(symbol, limit),
    enabled: !!symbol,
    staleTime: 1000 * 60 * 60, // 1 hour
  })
}

// ===== SUMMARY HOOKS =====

export function useEarningsSummary(symbol: string) {
  return useQuery({
    queryKey: [...EARNINGS_SUMMARY_KEY, symbol],
    queryFn: () => earningsSummaryApi.get(symbol),
    enabled: !!symbol,
    staleTime: 1000 * 60 * 60, // 1 hour
  })
}

// ===== REFRESH MUTATIONS =====

export function useRefreshEarningsCalendar() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (symbols?: string[]) => earningsRefreshApi.refreshCalendar(symbols),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EARNINGS_CALENDAR_KEY })
    },
  })
}

export function useRefreshEarningsHistory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (symbol: string) => earningsRefreshApi.refreshSymbol(symbol),
    onSuccess: (_, symbol) => {
      queryClient.invalidateQueries({ queryKey: [...EARNINGS_HISTORY_KEY, symbol] })
      queryClient.invalidateQueries({ queryKey: [...EARNINGS_SUMMARY_KEY, symbol] })
    },
  })
}
